/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { executeApi } from "../util/apiHelper";


const safeLog = payload => {
  executeApi("logs-recordClientEvent", { eventType: payload.action === "download_report" ? "download_requested" : payload.action, page: (payload.path || window.location.pathname).split("?")[0] }).catch(() => {});
};

const safePresence = (adminId, data) => {
  if (!adminId) return;
  executeApi("utils-updateAdminPresence", { adminId, presenceData: data }).catch(() => {});
};

const getDeviceInfo = () => {
  if (typeof window === "undefined" || !navigator) return { browser: "Unknown", os: "Unknown", text: "Desktop" };
  const ua = navigator.userAgent;

  let browser = "Chrome";
  if (ua.includes("Firefox/")) browser = "Firefox";
  else if (ua.includes("Edg/")) browser = "Edge";
  else if (ua.includes("Safari/") && !ua.includes("Chrome")) browser = "Safari";

  let os = "Desktop";
  if (ua.includes("Mac OS")) os = "macOS";
  else if (ua.includes("Windows")) os = "Windows";
  else if (ua.includes("Android")) os = "Android";
  else if (ua.includes("iPhone") || ua.includes("iPad")) os = "iOS";
  else if (ua.includes("Linux")) os = "Linux";

  return { browser, os, text: `${browser} • ${os}` };
};

const fetchGeoLocation = async () => {
  try {
    const cached = sessionStorage.getItem("admin_geo_info");
    if (cached) return JSON.parse(cached);

    const res = await fetch("https://ipwho.is/");
    const data = await res.json();
    if (data && data.success) {
      const geo = {
        ip: data.ip || "",
        city: data.city || "",
        region: data.region || "",
        country: data.country || "",
        countryCode: data.country_code || "TH",
        isp: data.connection?.isp || data.connection?.org || "",
        latitude: data.latitude || null,
        longitude: data.longitude || null,
        locationText: [data.city, data.region, data.country].filter(Boolean).join(", "),
      };
      sessionStorage.setItem("admin_geo_info", JSON.stringify(geo));
      return geo;
    }
  } catch (err) {
    console.warn("Geo lookup error:", err);
  }
  return null;
};

const getFriendlyPageName = (path) => {
  const cleanPath = (path || "").split("?")[0];
  const REPORT_MAP = {
    "/reports/daily-sale": "รายงานยอดขายประจำวัน",
    "/reports/order": "รายงานคำสั่งซื้อ",
    "/reports/winner": "รายงานผู้ถูกรางวัล",
    "/reports/affiliate": "รายงานตัวแทน/Affiliate",
    "/reports/admin-commission": "รายงานค่าคอมมิชชั่นแอดมิน",
    "/reports/coupon": "รายงานคูปอง",
    "/reports/shop": "รายงานร้านค้า",
    "/reports/tax-invoice": "รายงานใบกำกับภาษี",
    "/reports/tax-coupon": "รายงานภาษีคูปอง",
    "/reports/net-income": "รายงานรายได้สุทธิ",
    "/reports/all-user": "รายงานผู้ใช้งานทั้งหมด",
    "/reports/verified-user": "รายงานผู้ใช้งานยืนยันตัวตน",
    "/reports/user-year": "รายงานผู้ใช้รายปี",
    "/reports/user-prizes": "รายงานรางวัลผู้ใช้งาน",
    "/reports/reserve-order": "รายงานการจองคำสั่งซื้อ",
    "/reports/order-discount": "รายงานส่วนลดคำสั่งซื้อ",
    "/reports/promo-code-created": "รายงานการสร้างโค้ดโปรโมชั่น",
    "/reports/lotteries-books-import": "รายงานการนำเข้าเล่มสลาก",
    "/reports/utm": "รายงาน UTM Marketing",
    "/reports/button-click": "รายงานสถิติการคลิกปุ่ม",
    "/reports": "หน้ารายงาน (Reports)",
    "/orders/list": "รายการคำสั่งซื้อ",
    "/orders/reserve": "คำสั่งซื้อแบบจอง",
    "/orders/line": "คำสั่งซื้อผ่าน LINE",
    "/orders/offline": "คำสั่งซื้อออฟไลน์",
    "/orders/mail": "คำสั่งซื้อทางไปรษณีย์",
    "/orders": "จัดการคำสั่งซื้อ (Orders)",
    "/coupons/manage": "จัดการคูปอง",
    "/coupons/orders": "ออเดอร์คูปอง",
    "/coupons": "จัดการคูปอง",
    "/users/create": "สร้างข้อมูลผู้ใช้งาน",
    "/users": "จัดการข้อมูลผู้ใช้",
    "/admins": "จัดการแอดมินและสิทธิ์",
    "/lotteries/imported": "สลากที่นำเข้าแล้ว",
    "/lotteries/unimported": "สลากที่ยังไม่นำเข้า",
    "/lotteries/scanner": "เครื่องสแกนสลาก",
    "/lotteries": "จัดการสลาก",
    "/prizes": "ตรวจรางวัล",
    "/complains": "ข้อร้องเรียนลูกค้า",
    "/donates": "ระบบบริจาค",
    "/missions": "ภารกิจและของรางวัล",
    "/giftshops": "ร้านค้าของขวัญ",
    "/dashboard": "แดชบอร์ดสรุปผล",
    "/dev/monitor": "Dev Real-time Monitor",
    "/dev/all-in-one-draw": "All-in-One ปิดงวด",
    "/dev": "เครื่องมือ Dev",
  };

  for (const [key, label] of Object.entries(REPORT_MAP)) {
    if (cleanPath === key || cleanPath.startsWith(key + "/") || (key !== "/" && cleanPath.startsWith(key))) {
      return label;
    }
  }
  if (cleanPath === "/" || cleanPath === "") return "หน้าหลัก (Home)";
  return cleanPath;
};

export const AdminPresenceTracker = ({ profileState }) => {
  const location = useLocation();
  const lastHeartbeatRef = useRef(0);
  const idleTimerRef = useRef(null);
  const currentPathRef = useRef(location.pathname + (location.search || ""));
  const recentVisitsRef = useRef([]);
  const geoInfoRef = useRef(null);
  const lastNavLogRef = useRef({ path: "", time: 0 });

  // Initialize geo lookup (IP-based + HTML5 GPS) once per session
  useEffect(() => {
    // 1. Instant IP-based lookup
    fetchGeoLocation().then((geo) => {
      if (geo) {
        geoInfoRef.current = { ...geo, ...(geoInfoRef.current || {}) };
        sendPresence("online");
      }
    });

    // 2. High accuracy browser HTML5 GPS location
    if (typeof window !== "undefined" && navigator?.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude, accuracy } = pos.coords;
          geoInfoRef.current = {
            ...(geoInfoRef.current || {}),
            latitude,
            longitude,
            accuracy,
            isGPS: true,
          };
          sendPresence("online");
        },
        () => { },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    }
  }, []);

  currentPathRef.current = location.pathname + (location.search || "");

  // Resolve admin identifier reliably
  const adminId = profileState?.userId
    ? String(profileState.userId)
    : profileState?.username
      ? String(profileState.username)
      : profileState?._id
        ? String(profileState._id)
        : null;

  const sendPresence = async (status = "online", explicitPath = null) => {
    if (!adminId) return;

    try {
      const now = Date.now();
      lastHeartbeatRef.current = now;


      const fullName = [profileState.firstName, profileState.lastName].filter(Boolean).join(" ").trim() ||
        profileState.name ||
        profileState.username ||
        `Admin (${adminId})`;

      const phone = profileState.phone || profileState.tel || profileState.telephone || profileState.mobile || profileState.phoneNumber || "";
      const lineTitle = profileState.lineTitle || profileState.line_title || profileState.lineName || "";
      const device = getDeviceInfo();
      const path = explicitPath || currentPathRef.current || "/";

      // Track page navigation history
      if (explicitPath || recentVisitsRef.current.length === 0) {
        const lastEntry = recentVisitsRef.current[0];
        if (!lastEntry || lastEntry.path !== path || now - lastEntry.timestamp > 15000) {
          recentVisitsRef.current = [
            {
              path,
              timestamp: now,
              timeFormatted: new Date(now).toLocaleTimeString("th-TH"),
            },
            ...recentVisitsRef.current,
          ].slice(0, 25);
        }
      }

      safePresence(adminId, {
        userId: adminId,
        name: fullName,
        firstName: profileState.firstName || "Admin",
        lastName: profileState.lastName || "",
        phone,
        lineTitle,
        lineName: lineTitle,
        displayName: profileState.displayName || profileState.nickname || "",
        email: profileState.email || "",
        role: profileState.role || "admin",
        profileImage: profileState.profileImage || "",
        currentPath: path,
        recentVisits: recentVisitsRef.current,
        status, // 'online' | 'idle' | 'offline'
        device: device.text,
        browser: device.browser,
        os: device.os,
        geo: geoInfoRef.current || null,
        location: geoInfoRef.current?.locationText || "",
        city: geoInfoRef.current?.city || "",
        isp: geoInfoRef.current?.isp || "",
        ip: geoInfoRef.current?.ip || "",
        lastActiveMs: now,
      });
    } catch (err) {
      console.warn("AdminPresenceTracker failed:", err?.message || err);
    }
  };

  // 1. Path Change Trigger & Live Action Log
  useEffect(() => {
    if (!adminId) return;
    const current = location.pathname + (location.search || "");
    sendPresence("online", current);

    // Record page navigation to Logs collection
    const now = Date.now();
    if (lastNavLogRef.current.path !== current || now - lastNavLogRef.current.time > 10000) {
      lastNavLogRef.current = { path: current, time: now };
      const friendlyName = getFriendlyPageName(location.pathname);
      const fullName = [profileState?.firstName, profileState?.lastName].filter(Boolean).join(" ").trim() ||
        profileState?.name ||
        profileState?.nickname ||
        profileState?.username ||
        `Admin (${adminId})`;

      safeLog({
        createdAt: now,
        actor: String(adminId),
        name: fullName,
        type: "navigation",
        action: "view_page",
        page: friendlyName,
        path: current,
        target: current,
        detail: `เปิดเข้าหน้า: ${friendlyName} (${current})`,
        ip: geoInfoRef.current?.ip || "",
        location: geoInfoRef.current?.locationText || "",
        device: getDeviceInfo().text,
      });
    }
  }, [adminId, location.pathname, location.search]);


    // 2. Regular Heartbeat (every 3 minutes) + Activity Listener
  useEffect(() => {
    if (!adminId) return;

    // Send immediate initial presence
    sendPresence("online");

    const interval = setInterval(() => {
      sendPresence("online");
    }, 3 * 60 * 1000);

    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        sendPresence("idle");
      }, 5 * 60 * 1000); // 5 minutes of inactivity -> idle
    };

    const handleUserActivity = () => {
      const now = Date.now();
      resetIdleTimer();

      // If more than 90s since last heartbeat, update immediately
      if (now - lastHeartbeatRef.current > 90 * 1000) {
        sendPresence("online");
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        idleTimerRef.current = setTimeout(() => {
          sendPresence("idle");
        }, 90 * 1000); // 1.5 min in background -> idle
      } else {
        handleUserActivity();
        sendPresence("online");
      }
    };

    const handleBeforeUnload = () => {
      try {
        if (adminId) {
          safePresence(adminId, {
            status: "offline",
            lastActiveMs: Date.now(),
          });
        }
      } catch { }
    };

    // 3. Global Download, Search & Link Interceptor (Capture phase)
    const lastDownloadLogRef = { current: 0 };
    const lastSearchLogRef = { current: 0 };
    const lastLinkLogRef = { current: 0 };

    const handleGlobalClick = (event) => {
      handleUserActivity();

      try {
        const el = event.target;
        if (!el) return;

        const anchor = el.closest ? el.closest("a") : null;
        const button = el.closest ? el.closest("button") : null;
        const target = anchor || button || el;

        const downloadAttr = anchor?.getAttribute("download") || target.getAttribute?.("download");
        const href = anchor?.getAttribute("href") || "";
        const text = (target.innerText || target.textContent || "").trim();

        // Check A: Download / Export Action
        const isBlobOrCSV = href.startsWith("blob:") || href.includes(".csv") || href.includes("data:text/csv") || Boolean(downloadAttr);
        const isReportPage = location.pathname.includes("/report") || location.pathname.includes("/orders") || location.pathname.includes("/coupons") || location.pathname.includes("/dev");
        const isExportAction = /ดาวน์โหลด|export|download|csv|excel|นำออก|พิมพ์/i.test(text) || Boolean(target.getAttribute?.("data-export"));

        if (isBlobOrCSV || (isReportPage && isExportAction)) {
          const now = Date.now();
          if (now - lastDownloadLogRef.current < 1500) return;
          lastDownloadLogRef.current = now;

          const current = location.pathname + (location.search || "");
          const reportName = getFriendlyPageName(location.pathname);
          const fileName = downloadAttr || (text ? `${text.slice(0, 30)}.csv` : `${reportName}.csv`);

          const fullName = [profileState?.firstName, profileState?.lastName].filter(Boolean).join(" ").trim() ||
            profileState?.name ||
            profileState?.nickname ||
            profileState?.username ||
            "Admin";

          safeLog({
            createdAt: now,
            actor: String(adminId),
            name: fullName,
            type: "reports",
            action: "download_report",
            reportName,
            fileName,
            path: current,
            target: fileName,
            detail: `ดาวน์โหลดรายงาน: ${reportName} (${fileName})`,
          });
          return;
        }

        // Check B: Search Button Click
        const isSearchButton = (/ค้นหา|search/i.test(text) || target.getAttribute?.("data-search")) && (button || target.tagName === "BUTTON");
        if (isSearchButton) {
          const now = Date.now();
          if (now - lastSearchLogRef.current < 1500) return;
          lastSearchLogRef.current = now;

          // Search container (find parent card/form/grid/flex)
          const container = button?.closest("form, .card, [class*='grid'], [class*='flex'], [class*='bg-white'], main, section") || document;
          const inputs = Array.from(container.querySelectorAll("input[type='text'], input[type='search'], input[type='tel'], input[type='number'], input:not([type='hidden'])"));
          const selects = Array.from(container.querySelectorAll("select"));

          // Gather non-empty inputs
          const filledInputs = inputs.filter((inp) => inp.value && inp.value.trim().length > 0);

          // Gather active select labels
          const selectLabels = selects
            .map((s) => {
              if (s.selectedIndex >= 0 && s.options[s.selectedIndex]) {
                const optText = s.options[s.selectedIndex].text;
                return optText && !/ทั้งหมด|all/i.test(optText) ? optText : null;
              }
              return null;
            })
            .filter(Boolean);

          let queryVal = "";
          if (filledInputs.length > 0) {
            queryVal = filledInputs
              .map((inp) => {
                const placeholder = inp.getAttribute("placeholder");
                const label = placeholder && placeholder !== "ค้นหา" ? `[${placeholder}] ` : "";
                return `${label}${inp.value.trim()}`;
              })
              .join(", ");
          } else {
            // Check any filled input across document
            const allDocFilled = Array.from(document.querySelectorAll("input:not([type='hidden'])")).filter((i) => i.value && i.value.trim().length > 0);
            if (allDocFilled.length > 0) {
              queryVal = allDocFilled.map((i) => i.value.trim()).join(", ");
            }
          }

          const searchTypeLabel = selectLabels.join(" • ");
          const pageTitle = getFriendlyPageName(location.pathname);
          const current = location.pathname + (location.search || "");

          const searchFormatted = queryVal
            ? (searchTypeLabel ? `ค้นหา (${searchTypeLabel}): "${queryVal}"` : `ค้นหาคำว่า: "${queryVal}"`)
            : (searchTypeLabel ? `ค้นหาตามตัวกรอง (${searchTypeLabel})` : "ค้นหาตามเงื่อนไขที่เลือก");

          const fullName = [profileState?.firstName, profileState?.lastName].filter(Boolean).join(" ").trim() ||
            profileState?.name ||
            profileState?.nickname ||
            profileState?.username ||
            "Admin";

          safeLog({
            createdAt: now,
            actor: String(adminId),
            name: fullName,
            type: "search",
            action: "search_query",
            searchTerm: queryVal || searchTypeLabel || "ค้นหาตามเงื่อนไข",
            searchType: searchTypeLabel || "ทั่วไป",
            page: pageTitle,
            path: current,
            target: queryVal || searchTypeLabel || pageTitle,
            detail: `ค้นหาในหน้า [${pageTitle}]: ${searchFormatted}`,
            ip: geoInfoRef.current?.ip || "",
            location: geoInfoRef.current?.locationText || "",
            device: getDeviceInfo().text,
          });
          return;
        }

        // Check C: Outbound / External Link Click
        if (anchor && href && (href.startsWith("http") || anchor.target === "_blank") && !href.includes(window.location.host)) {
          const now = Date.now();
          if (now - lastLinkLogRef.current < 2000) return;
          lastLinkLogRef.current = now;

          const current = location.pathname + (location.search || "");
          const pageTitle = getFriendlyPageName(location.pathname);
          const fullName = [profileState?.firstName, profileState?.lastName].filter(Boolean).join(" ").trim() ||
            profileState?.name ||
            profileState?.nickname ||
            profileState?.username ||
            "Admin";

          safeLog({
            createdAt: now,
            actor: String(adminId),
            name: fullName,
            type: "link_click",
            action: "click_link",
            page: pageTitle,
            path: current,
            target: href,
            detail: `คลิกลิงก์ภายนอก: ${text ? `"${text}" -> ` : ""}${href}`,
            linkUrl: href,
            ip: geoInfoRef.current?.ip || "",
            location: geoInfoRef.current?.locationText || "",
            device: getDeviceInfo().text,
          });
        }
      } catch { }
    };

    const handleGlobalKeyDown = (event) => {
      handleUserActivity();

      try {
        if (event.key === "Enter" && event.target?.tagName === "INPUT") {
          const queryVal = (event.target.value || "").trim();
          if (!queryVal) return;

          const now = Date.now();
          if (now - lastSearchLogRef.current < 1500) return;
          lastSearchLogRef.current = now;

          const container = event.target.closest("form, .card, [class*='grid'], [class*='flex'], [class*='bg-white'], main, section") || document;
          const selects = Array.from(container.querySelectorAll("select"));
          let searchTypeLabel = "";
          if (selects.length > 0) {
            const activeSelect = selects.find((s) => s.selectedIndex >= 0 && s.options[s.selectedIndex]);
            if (activeSelect && activeSelect.options[activeSelect.selectedIndex]) {
              searchTypeLabel = activeSelect.options[activeSelect.selectedIndex].text;
            }
          }

          const placeholder = event.target.getAttribute("placeholder");
          const pageTitle = getFriendlyPageName(location.pathname);
          const current = location.pathname + (location.search || "");
          const searchFormatted = searchTypeLabel && !/ทั้งหมด|all/i.test(searchTypeLabel)
            ? `ค้นหา (${searchTypeLabel}): "${queryVal}"`
            : (placeholder && placeholder !== "ค้นหา" ? `ค้นหา (${placeholder}): "${queryVal}"` : `ค้นหาคำว่า: "${queryVal}"`);

          const fullName = [profileState?.firstName, profileState?.lastName].filter(Boolean).join(" ").trim() ||
            profileState?.name ||
            profileState?.nickname ||
            profileState?.username ||
            "Admin";

          safeLog({
            createdAt: now,
            actor: String(adminId),
            name: fullName,
            type: "search",
            action: "search_query",
            searchTerm: queryVal,
            searchType: searchTypeLabel || placeholder || "Enter Search",
            page: pageTitle,
            path: current,
            target: queryVal,
            detail: `ค้นหา (กด Enter) ในหน้า [${pageTitle}]: ${searchFormatted}`,
            ip: geoInfoRef.current?.ip || "",
            location: geoInfoRef.current?.locationText || "",
            device: getDeviceInfo().text,
          });
        }

      } catch { }
    };

    window.addEventListener("mousemove", handleUserActivity, { passive: true });
    window.addEventListener("keydown", handleGlobalKeyDown, { passive: true });
    window.addEventListener("click", handleGlobalClick, true);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      clearInterval(interval);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      window.removeEventListener("mousemove", handleUserActivity);
      window.removeEventListener("keydown", handleGlobalKeyDown);
      window.removeEventListener("click", handleGlobalClick, true);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [adminId]);

  return null;
};

export default AdminPresenceTracker;
